import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Shield, UserCog, Crown, ChefHat, Briefcase, CreditCard, ClipboardList } from "lucide-react";

const ICONOS = {
  dueno: Crown,
  ventas: Briefcase,
  coordinador: ClipboardList,
  chef: ChefHat,
  mesero: UserCog,
  cajero: CreditCard,
};

const COLORES = {
  dueno: "#7c3aed",
  ventas: "#0ea5e9",
  coordinador: "#16a34a",
  chef: "#ea580c",
  mesero: "#db2777",
  cajero: "#f59e0b",
};

const RolesInternos = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState({});

  const esCelebraGroup = !!user?.es_celebra_group || user?.role === "superadmin";

  const cargar = useCallback(async () => {
    if (!esCelebraGroup) { setLoading(false); return; }
    setLoading(true);
    try {
      const [rRoles, rUsers] = await Promise.all([
        axios.get(`${API}/admin/roles`),
        axios.get(`${API}/admin/usuarios-empresa`),
      ]);
      setRoles(rRoles.data || {});
      setUsuarios(rUsers.data || []);
    } catch (e) {
      toast.error("No se pudieron cargar roles · " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [esCelebraGroup]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarRol = async (userId, nuevoRol) => {
    setGuardando(g => ({ ...g, [userId]: true }));
    try {
      await axios.put(`${API}/admin/usuarios/${userId}/rol-interno`, { rol_interno: nuevoRol || "" });
      setUsuarios(us => us.map(u => u.user_id === userId ? { ...u, rol_interno: nuevoRol || null, modulos_rol: nuevoRol && nuevoRol !== "dueno" ? roles[nuevoRol]?.modulos : null } : u));
      toast.success(nuevoRol ? `Rol asignado: ${roles[nuevoRol]?.label}` : "Rol removido");
    } catch (e) {
      toast.error("Error al asignar rol · " + (e?.response?.data?.detail || e.message));
    } finally {
      setGuardando(g => ({ ...g, [userId]: false }));
    }
  };

  const puedeEditar = user?.role === "superadmin" || user?.rol_interno === "dueno" || user?.workspace_rol === "admin";

  if (!esCelebraGroup) {
    return (
      <div className="p-8 max-w-xl mx-auto" data-testid="page-roles-no-access">
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-6 text-center">
            <Shield className="w-10 h-10 mx-auto text-amber-700 mb-3" />
            <h2 className="font-display text-xl text-stone-900 mb-1">Acceso restringido</h2>
            <p className="text-sm text-stone-700">Esta sección está disponible únicamente para usuarios de Celebra Group.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="p-6 text-sm text-stone-500">Cargando roles…</div>;

  return (
    <div className="space-y-6" data-testid="page-roles-internos">
      <div>
        <h1 className="font-display text-3xl text-stone-900 flex items-center gap-2">
          <Shield className="w-7 h-7 text-emerald-700" /> Roles internos
        </h1>
        <p className="text-sm text-stone-600 mt-1">
          Asigna roles a los empleados de Celebra Group para controlar a qué módulos acceden. El Dueño tiene acceso total; los demás roles ven solo los módulos de su área.
        </p>
      </div>

      {/* Catálogo de roles */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Catálogo de roles disponibles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(roles).map(([key, r]) => {
              const Icon = ICONOS[key] || Shield;
              const color = COLORES[key] || "#6b7280";
              return (
                <div key={key} className="p-4 rounded-xl border border-stone-200 bg-stone-50" data-testid={`rol-card-${key}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: color + "22" }}>
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <div>
                      <p className="font-display text-lg" style={{ color }}>{r.label}</p>
                      <p className="text-[10px] uppercase tracking-wide text-stone-500">Clave: {key}</p>
                    </div>
                  </div>
                  <p className="text-xs text-stone-600">
                    {r.modulos === "*" ? "Acceso total a todos los módulos" : `${r.modulos.length} módulo${r.modulos.length !== 1 ? "s" : ""} visibles`}
                  </p>
                  {Array.isArray(r.modulos) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {r.modulos.map(m => <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Lista de usuarios */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Empleados ({usuarios.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={cargar} data-testid="btn-recargar-usuarios">Recargar</Button>
        </CardHeader>
        <CardContent>
          {!puedeEditar && (
            <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              Solo el Superadmin o el Dueño pueden cambiar roles. Estás viendo en modo lectura.
            </div>
          )}
          {usuarios.length === 0 ? (
            <p className="text-sm text-stone-500 py-6 text-center">No hay usuarios en tu empresa.</p>
          ) : (
            <div className="space-y-2">
              {usuarios.map(u => {
                const rolKey = u.rol_interno;
                const Icon = ICONOS[rolKey] || UserCog;
                const color = COLORES[rolKey] || "#6b7280";
                return (
                  <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 bg-white hover:bg-stone-50" data-testid={`usuario-${u.user_id}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ background: color }}>
                      {(u.name || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{u.name || "Sin nombre"}</p>
                      <p className="text-xs text-stone-500 truncate">{u.email}</p>
                      {u.role === "superadmin" && <Badge className="mt-1 bg-purple-600 text-white text-[10px]">Superadmin</Badge>}
                    </div>
                    {rolKey && (
                      <div className="hidden sm:flex items-center gap-1">
                        <Icon className="w-4 h-4" style={{ color }} />
                        <span className="text-sm font-medium" style={{ color }}>{roles[rolKey]?.label}</span>
                      </div>
                    )}
                    <Select
                      disabled={!puedeEditar || guardando[u.user_id] || u.role === "superadmin"}
                      value={rolKey || "_none"}
                      onValueChange={(v) => cambiarRol(u.user_id, v === "_none" ? "" : v)}
                    >
                      <SelectTrigger className="w-44 border-stone-300" data-testid={`select-rol-${u.user_id}`}>
                        <SelectValue placeholder="Sin rol" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Sin rol</SelectItem>
                        {Object.entries(roles).map(([k, r]) => (
                          <SelectItem key={k} value={k}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RolesInternos;
